import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAllPublicTenantProfiles, fetchMultipleTenantProfiles } from "../api/tenantProfileApi";

import docImg from "../Images/doclift.png";
import vigilImg from "../Images/vigil.png";
import saasImg from "../Images/Aaas.png";
import railplotImg from "../Images/railplot.png";
import vxiomaticImg from "../Images/vxiomatic.png";
import vyntraImg from "../Images/vyntra.png";

const defaultServices = [
  { image: docImg, title: "DocLift", description: "DocLift enables businesses to automate WhatsApp interactions, streamline appointment and service request management, and gain insights into physical space operations." },
  { image: vxiomaticImg, title: "Vxiomatic", description: "Vxiomatic is a proprietary SaaS platform by Orryworx Pvt. Ltd., providing cloud-based OTT content management, monetization and secure content delivery." },
  { image: saasImg, title: "Aaas", description: "Authorization-as-a-Service (AaaS) provides secure, policy-based access control for any datasource through configurable APIs. It enables data sharing with fine-grained authorization, masking, and threat protection." },
  { image: railplotImg, title: "RailPlot", description: "Our railway analytics product. It turns raw locomotive speedometer data from Laxven, Medha, Raitech, and Telpro into live dashboards — speed violations, pre-braking analysis, and crew management in one place." },
  { image: vigilImg, title: "Vigil", description: "Our AI-powered vigilance system that detects and alerts on identified individuals, enabling real-time tracking across multiple cameras to monitor movement and enhance security." },
  { image: vyntraImg, title: "Vyntra", description: "Vyntra is a proprietary technology product developed and owned by Orryworx Pvt. Ltd., delivering modern and scalable digital solutions for businesses." },
];

const CARD_GAP = 320; // horizontal distance between each card's center

const Product = ({ tenantIds = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [apiCards, setApiCards] = useState([]);

  // Create a primitive dependency key to prevent infinite useEffect loops on array reference change
  const tenantIdsKey = Array.isArray(tenantIds) ? tenantIds.join(",") : "";

  useEffect(() => {
    let isMounted = true;

    async function loadAllTenants() {
      try {
        let profiles = [];
        if (tenantIdsKey) {
          profiles = await fetchMultipleTenantProfiles(tenantIds);
        } else {
          profiles = await fetchAllPublicTenantProfiles();
        }

        if (isMounted && Array.isArray(profiles) && profiles.length > 0) {
          // STRICT FILTER: Only include profiles that have ALL THREE: heading, logoUrl, and description
          const validProfiles = profiles.filter((p) => {
            const hasHeading = Boolean(p.heading && p.heading.trim());
            const hasLogo = Boolean(p.logoUrl && p.logoUrl.trim());
            const hasDescription = Boolean(p.description && p.description.trim());
            return hasHeading && hasLogo && hasDescription;
          });

          const formattedCards = validProfiles.map((p) => ({
            id: p.id,
            title: p.heading,
            description: p.description,
            image: p.logoUrl,
            isDynamic: true,
          }));

          setApiCards(formattedCards);
        }
      } catch (err) {
        console.error("Failed to load tenant profile cards:", err);
      }
    }

    loadAllTenants();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantIdsKey]);

  // Keep original starting order: default products first, appended with new complete API cards
  const allServices = [...defaultServices, ...apiCards];
  const total = allServices.length;

  useEffect(() => {
    if (paused || total === 0) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev >= total - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [paused, total]);

  const nextSlide = () => setActiveIndex((prev) => (prev >= total - 1 ? 0 : prev + 1));
  const prevSlide = () => setActiveIndex((prev) => (prev === 0 ? total - 1 : prev - 1));

  return (
    <section className="w-full bg-[#f3f5f7] py-24 overflow-hidden">
      <div className="text-center mb-16">
        <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
          Proprietary SaaS & Platforms
        </h2>

        <div className="w-24 h-1 bg-blue-600 mx-auto mt-5 rounded-full"></div>

        <p className="max-w-4xl mx-auto mt-8 text-lg text-gray-600 leading-relaxed">
          Our proprietary SaaS platforms combine cutting-edge technology, intuitive design, and robust architecture to deliver tailored solutions that drive productivity, collaboration, and business growth.
        </p>
      </div>

      <div className="relative w-full max-w-7xl mx-auto flex flex-col items-center">
        <div className="relative flex items-center justify-center w-full min-h-[550px]">
          {allServices.map((service, index) => {
            // Shortest signed distance from activeIndex, wraps both directions
            let offset = index - activeIndex;
            if (offset > total / 2) offset -= total;
            if (offset < -total / 2) offset += total;

            const absOffset = Math.abs(offset);
            const isCenter = absOffset === 0;
            const isAdjacent = absOffset === 1;

            const translateX = offset * CARD_GAP;
            const scale = isCenter ? 1 : isAdjacent ? 0.75 : 0.6;
            const opacity = isCenter ? 1 : isAdjacent ? 0.5 : 0;
            const zIndex = 20 - absOffset;
            const pointerEvents = absOffset <= 1 ? "auto" : "none";
            const size = isCenter
              ? { width: 380, height: 500 }
              : { width: 260, height: 400 };

            return (
              <div
                key={service.id || index}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                style={{
                  transform: `translateX(${translateX}px) scale(${scale})`,
                  opacity,
                  zIndex,
                  pointerEvents,
                  width: size.width,
                  height: size.height,
                  transition: "transform 0.7s ease-in-out, opacity 0.7s ease-in-out, width 0.7s ease-in-out, height 0.7s ease-in-out",
                }}
                className="absolute rounded-3xl overflow-hidden bg-white shadow-xl flex flex-col items-center text-center cursor-pointer"
              >
                <div className="w-full h-52 bg-slate-50 flex items-center justify-center overflow-hidden p-2">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover rounded-t-3xl"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = docImg;
                    }}
                  />
                </div>
                <div className="p-6 flex flex-col items-center flex-grow">
                  <h3 className="text-2xl font-bold text-black flex items-center gap-2">
                    {service.title}
                    {service.isDynamic && (
                      <span className="text-[10px] uppercase font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Live API
                      </span>
                    )}
                  </h3>
                  <p className="mt-4 text-gray-600 leading-7 text-[15px] line-clamp-4">
                    {service.description}
                  </p>
                  <Link
                    to="/portfolio"
                    className="mt-auto pt-4 inline-block text-blue-600 underline hover:text-blue-800 transition"
                  >
                    Know More
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-6 mt-10">
          <button onClick={prevSlide} aria-label="Previous Slide" className="bg-white shadow-xl w-14 h-14 rounded-full text-3xl hover:scale-110 transition-all duration-300">←</button>
          <button onClick={nextSlide} aria-label="Next Slide" className="bg-white shadow-xl w-14 h-14 rounded-full text-3xl hover:scale-110 transition-all duration-300">→</button>
        </div>
      </div>
    </section>
  );
};

export default Product;